import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const [activeSort, setActiveSort] = useState<'rating' | 'views' | 'newest'>('rating');

  const categoryComics = useMemo(() => {
    if (!category) return [];
    const list = category.comicIds
      .map((id) => comics.find((c) => c.id === id))
      .filter(Boolean);

    const sorted = [...list];
    if (activeSort === 'rating') {
      sorted.sort((a, b) => (b?.rating || 0) - (a?.rating || 0));
    } else if (activeSort === 'views') {
      sorted.sort((a, b) => (b?.views || 0) - (a?.views || 0));
    } else if (activeSort === 'newest') {
      sorted.sort((a, b) => new Date(b?.updateTime || '').getTime() - new Date(a?.updateTime || '').getTime());
    }
    return sorted;
  }, [category, activeSort]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const otherCategories = categories.filter((c) => c.slug !== slug);

  return (
    <>
      <SEOSchema
        type="WebPage"
        data={{
          name: `${category.name} - 韩漫漫画网站`,
          description: category.description,
          url: `https://www.hanmancomic.com/category/${category.slug}`,
        }}
      />

      <Navbar />

      <main className="min-h-screen pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mt-6 mb-10">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100">
                <i className={`${category.icon} text-accent-600 text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-2 bg-background-100 rounded-full p-1">
              {(['rating', 'views', 'newest'] as const).map((sort) => (
                <button
                  key={sort}
                  onClick={() => setActiveSort(sort)}
                  className={`px-4 py-1.5 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
                    activeSort === sort
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {sort === 'rating' && '评分最高'}
                  {sort === 'views' && '人气最热'}
                  {sort === 'newest' && '最近更新'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {categoryComics.map((comic) => comic && (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-100 mb-3">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        title={`${comic.title} - ${category.name}韩漫`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-label ${
                          comic.status === '连载中' ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'bg-foreground-600 text-background-50'
                        }`}>
                          {comic.status}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-label font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      <span className="text-xs text-foreground-500">{comic.rating}</span>
                      <span className="text-xs text-foreground-400">· {comic.chapters}话</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <aside className="lg:w-64 flex-shrink-0">
              <div className="bg-background-100 rounded-lg p-5 sticky top-20">
                <h3 className="font-label font-medium text-sm text-foreground-900 mb-4">其他分类</h3>
                <div className="flex flex-col gap-2">
                  {otherCategories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                        <i className={`${cat.icon} text-accent-600 text-sm`}></i>
                      </div>
                      <span className="text-sm text-foreground-700 font-label whitespace-nowrap">{cat.name}</span>
                      <span className="text-xs text-foreground-400 ml-auto">{cat.comicIds.length}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}